import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { Accordion, AccordionItem } from '@carbon/react';
import { format } from 'date-fns';
import {
  createCountyCoverageSearchAtom,
  countyCoverageSummaryAtom,
} from './atoms';
import { CollapsibleFilterPanel } from 'components/collapsible-filter-panel';
import { DynamicDateFilter } from 'components/dynamic-date-filter';
import { StateCountyFilters } from './state-county-filters';
import { SummaryCards, type CountyCoverageCardType } from './summary-cards';
import { CountyCoverageTable } from './county-coverage-table';
import { getStartDate } from './utils';
import { useCountyCoverageFilters } from './hooks';
import styles from './county-coverage.module.scss';

/**
 * The county coverage page: summary cards, the filter panel and the table.
 * @return {JSX.Element} The page.
 */
export function CountyCoverageContent() {
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  const { searchAtom } = useMemo(() => createCountyCoverageSearchAtom(100), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const [summaryState, fetchSummary] = useAtom(countyCoverageSummaryAtom);

  // Create a stable key from only filter params to prevent unnecessary API calls
  const payload = searchResult.currentPayload;
  const stateIds = payload?.stateIds;
  const countyIds = payload?.countyIds;
  const dateFilter = payload?.dateFilter;
  const text = payload?.text;

  const filterParams = useMemo(() => {
    if (!payload) return null;
    return {
      stateIds,
      countyIds,
      dateFilter,
      text,
    };
  }, [stateIds, countyIds, dateFilter, text, payload]);

  const filterParamsKey = useMemo(() => {
    if (!filterParams) return null;
    return JSON.stringify({
      stateIds: filterParams.stateIds?.sort(),
      countyIds: filterParams.countyIds?.sort(),
      dateFilter: filterParams.dateFilter,
      text: filterParams.text,
    });
  }, [filterParams]);

  const previousFilterParamsRef = useRef<string | null>(null);

  useEffect(() => {
    // Only fetch if filter params actually changed
    if (filterParamsKey === previousFilterParamsRef.current) {
      return;
    }
    previousFilterParamsRef.current = filterParamsKey;
    // Don't fetch if no payload yet
    if (!filterParamsKey || !searchResult.currentPayload) {
      return;
    }

    fetchSummary({
      url: '/queries/county_coverage/summary',
      type: 'POST',
      data: {
        stateIds: searchResult.currentPayload.stateIds,
        countyIds: searchResult.currentPayload.countyIds,
        dateFilter: searchResult.currentPayload.dateFilter,
        text: searchResult.currentPayload.text,
      },
    });
  }, [filterParamsKey, fetchSummary, searchResult.currentPayload]);

  const filterCount = useMemo(() => {
    let count = 0;
    const payload = searchResult.currentPayload;
    if (!payload) return 0;
    if (payload.dateFilter) count++;
    if (payload.stateIds && payload.stateIds.length > 0)
      count += payload.stateIds.length;
    if (payload.countyIds && payload.countyIds.length > 0)
      count += payload.countyIds.length;
    return count;
  }, [searchResult.currentPayload]);

  const {
    handleDateFilterChange,
    handleStateIdsChange,
    handleCountyIdsChange,
    handleResetFilters,
  } = useCountyCoverageFilters(setSearchPayload);

  // Handler for summary card filter icon clicks (summary cards count by created_at)
  const handleSummaryCardClick = useCallback(
    (card: CountyCoverageCardType) => {
      const endDate = new Date();
      const startDate = getStartDate(card, endDate);
      handleDateFilterChange({
        type: 'created_at',
        start: format(startDate, 'yyyy-MM-dd'),
        end: format(endDate, 'yyyy-MM-dd'),
      });
    },
    [handleDateFilterChange]
  );

  const stats =
    summaryState.result?.type === 'success'
      ? summaryState.result.data
      : undefined;

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.summaryCardsWrapper}>
        <SummaryCards stats={stats} onCardClick={handleSummaryCardClick} />
      </div>
      <div className={styles.pageLayout}>
        <CollapsibleFilterPanel
          filterCount={filterCount}
          onReset={handleResetFilters}
          isOpen={isFilterOpen}
        >
          <Accordion className={styles.filterAccordion}>
            <AccordionItem
              title="Date Filter"
              open={true}
              className={styles.filterAccordionItem}
            >
              <DynamicDateFilter
                value={payload?.dateFilter}
                onChange={handleDateFilterChange}
                dateOptions={[
                  { value: 'created_at', label: 'Created Date' },
                  { value: 'file_date', label: 'File Date' },
                  { value: 'effective_date', label: 'Effective Date' },
                  { value: 'instrument_date', label: 'Instrument Date' },
                  { value: 'recorded_date', label: 'Recorded Date' },
                ]}
              />
            </AccordionItem>
            <AccordionItem
              title="Geographic Filter"
              open={true}
              className={styles.filterAccordionItem}
            >
              <StateCountyFilters
                facets={searchResult.data?.facets}
                selectedStateIds={payload?.stateIds ?? []}
                selectedCountyIds={payload?.countyIds ?? []}
                onStateIdsChange={handleStateIdsChange}
                onCountyIdsChange={handleCountyIdsChange}
              />
            </AccordionItem>
          </Accordion>
        </CollapsibleFilterPanel>
        <div className={styles.mainContent}>
          <CountyCoverageTable
            searchResult={searchResult}
            setSearchPayload={setSearchPayload}
            isFilterOpen={isFilterOpen}
            onToggleFilter={() => setIsFilterOpen(!isFilterOpen)}
            filterCount={filterCount}
          />
        </div>
      </div>
    </div>
  );
}
